/**
 * Pipeline de imagen del panel: todo el trabajo pesado ocurre ACÁ, en el navegador.
 *
 * Motivo: el plan free de Cloudflare Workers da 10 ms de CPU por invocación y un
 * encoder WASM tarda cientos de ms. Comprimir en el cliente además hace la subida
 * mucho más liviana, que importa cuando se sube desde el celular con datos móviles.
 *
 * Medido con una foto real de celular (4032x3024, 2911 KB):
 *   WebP q0.82 -> ~60 KB (50x más chica) e indistinguible al tamaño de la card.
 */

export const ANCHO = 800;
export const ALTO = 1066; // 3:4, la proporción de la card del sitio
export const CALIDAD_WEBP = 0.82;
export const CALIDAD_JPEG = 0.85;
export const MAX_BYTES = 300 * 1024; // el mismo tope que valida el endpoint

/**
 * Calcula el recorte 3:4 centrado, con un desplazamiento ajustable.
 *
 * Función PURA a propósito: es la única parte con aritmética que se puede
 * equivocar, así que va testeada aparte (ver imagen.test.js).
 *
 * @param {number} w ancho de la imagen original
 * @param {number} h alto de la imagen original
 * @param {number} offset 0..1 — 0.5 es centrado; mueve el recorte sobre el eje que sobra
 * @returns {{sx:number, sy:number, sw:number, sh:number, recortado:'ancho'|'alto'|'nada'}}
 */
export function calcularRecorte(w, h, offset = 0.5) {
  const objetivo = ANCHO / ALTO;
  const actual = w / h;
  const o = Math.min(1, Math.max(0, offset));

  // Tolerancia: si ya está en proporción, no recortamos ni un pixel.
  if (Math.abs(actual - objetivo) < 0.001) {
    return { sx: 0, sy: 0, sw: w, sh: h, recortado: 'nada' };
  }

  if (actual > objetivo) {
    // Sobra ancho (foto horizontal): recortamos a los lados.
    const sw = Math.round(h * objetivo);
    return { sx: Math.round((w - sw) * o), sy: 0, sw, sh: h, recortado: 'ancho' };
  }
  // Sobra alto: recortamos arriba/abajo.
  const sh = Math.round(w / objetivo);
  return { sx: 0, sy: Math.round((h - sh) * o), sw: w, sh, recortado: 'alto' };
}

/** Error con mensaje ya listo para mostrarle a la dueña. */
export class ErrorImagen extends Error {}

/**
 * Decodifica el archivo respetando la orientación EXIF.
 *
 * `imageOrientation: 'from-image'` es lo que evita que las fotos de celular salgan
 * giradas. Chrome ya lo hace por defecto, pero se deja explícito para Safari y
 * navegadores viejos.
 */
export async function decodificar(file) {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new ErrorImagen(
      'Este navegador no pudo leer la foto. Si la tomaste con iPhone puede estar en ' +
        'formato HEIC: volvé a tomarla o guardala como JPG y probá de nuevo.'
    );
  }
}

/** Dibuja el recorte en un canvas del tamaño final. */
function dibujar(bitmap, recorte, Canvas) {
  const canvas = new Canvas(ANCHO, ALTO);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const { sx, sy, sw, sh } = recorte;
  ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, ANCHO, ALTO);
  return canvas;
}

/** Vista previa (data URL) de lo que se va a guardar, para que vea qué se recorta. */
export async function previsualizar(bitmap, offset = 0.5) {
  const canvas = dibujar(bitmap, calcularRecorte(bitmap.width, bitmap.height, offset), OffscreenCanvas);
  const blob = await canvas.convertToBlob({ type: 'image/webp', quality: 0.7 });
  return URL.createObjectURL(blob);
}

/**
 * Comprime de verdad: recorta, redimensiona y encodea.
 * Devuelve WebP; si el navegador no lo encodea, cae a JPEG.
 */
export async function comprimir(bitmap, offset = 0.5) {
  const canvas = dibujar(bitmap, calcularRecorte(bitmap.width, bitmap.height, offset), OffscreenCanvas);

  let blob = await canvas.convertToBlob({ type: 'image/webp', quality: CALIDAD_WEBP });
  // Algunos navegadores ignoran el tipo pedido y devuelven PNG: eso pesa muchísimo.
  if (blob.type !== 'image/webp') {
    blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: CALIDAD_JPEG });
  }
  if (blob.type !== 'image/webp' && blob.type !== 'image/jpeg') {
    throw new ErrorImagen('Este navegador no pudo comprimir la foto. Probá desde otro.');
  }
  if (blob.size > MAX_BYTES) {
    throw new ErrorImagen(
      `La foto quedó en ${Math.round(blob.size / 1024)} KB y el máximo es ` +
        `${Math.round(MAX_BYTES / 1024)} KB. Probá con una foto más simple.`
    );
  }
  return blob;
}
